import { useRef, useState } from 'react';
import { Add, ExpandMoreOutlined, Link, LinkOff } from '@mui/icons-material';
import {
  alpha,
  Box,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Popper,
  styled,
  Typography,
} from '@mui/material';
import { Row, Column } from 'further-ui/components/Layout';

const selectorWidth = '400px';

const BankAccountSelectStyled = styled(MenuList)(({ theme }) => ({
  maxWidth: selectorWidth,
  border: `1px solid ${theme.palette.grey[300]}`,
  borderBottom: 'none',
  padding: 0,
  gap: 0,
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.lg),
  borderBottom: `1px solid ${theme.palette.grey[300]}`,

  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),

    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },
  },
}));

const AddIcon = styled(Add)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: theme.fontSizes.xxl,
}));

const Container = styled(Box)(({ theme }) => ({
  position: 'relative',
  maxWidth: selectorWidth,
  flexGrow: 1,
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

const MenuContainer = styled(Box)(({ theme }) => ({
  maxWidth: selectorWidth,
  width: '100%',
  borderRadius: 0,
  border: 'none',
  backgroundColor: theme.palette.common.white,

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

const Account: React.FC<{
  onClick: () => void;
  selected?: boolean;
  accountName: string;
  iban?: string | null;
  accountNumber?: string;
  bankFeedAccountId?: string | null;
}> = ({
  onClick,
  accountName,
  iban,
  bankFeedAccountId,
  selected,
  accountNumber,
}) => (
  <MenuItemStyled onClick={onClick}>
    <Column spacing="sm" fullWidth>
      <Row centered spacing="sm" justify="space-between">
        <Typography variant="h5">
          <Row spacing="xs" centered>
            {accountName}
            {bankFeedAccountId ? (
              <Link color="primary" />
            ) : (
              <LinkOff color="error" />
            )}
          </Row>
        </Typography>
        {selected ? <ExpandMoreOutlined color="primary" /> : null}
      </Row>
      <Typography color="textSecondary" variant="body1">
        Account number / IBAN
        <br />
        {iban ?? accountNumber}
      </Typography>
    </Column>
  </MenuItemStyled>
);

const AddAccount: React.FC<{
  onClick: () => void;
  title: string;
  description: string;
}> = ({ onClick, title, description }) => (
  <MenuItemStyled selected={true} onClick={onClick}>
    <Row spacing="sm">
      <AddIcon />
      <Column spacing="sm">
        <Typography variant="h5" color="primary">
          {title}
        </Typography>
        <Typography color="textSecondary" variant="body2">
          {description}
        </Typography>
      </Column>
    </Row>
  </MenuItemStyled>
);

type Props = {
  selectedBankAccountId?: string;
  bankAccounts: Array<{
    id: string;
    bankName: string;
    iban?: string | null;
    accountNumber?: string;
    accountName: string;
    bankFeedAccountId?: string | null;
  }>;
  onBankAccountChange: (bankAccountId: string) => void;
  onBankAccountAdd: (useFeed: boolean) => void;
};

const menuId = 'bank-account-select-menu';

const BankAccountSelect: React.FC<Props> = ({
  bankAccounts,
  onBankAccountAdd,
  onBankAccountChange,
  selectedBankAccountId,
}) => {
  const anchorRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);

  const handleClose = (event: Event) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const handleMenuItemClick = (accountId: string) => {
    onBankAccountChange(accountId);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const selectedBankAccount = bankAccounts.find(
    (bankAccount) => bankAccount.id === selectedBankAccountId,
  );

  const availableBankAccounts = bankAccounts.filter(
    (bankAccount) => bankAccount.id !== selectedBankAccountId,
  );

  return (
    <Container>
      <BankAccountSelectStyled id={menuId} ref={anchorRef}>
        {selectedBankAccount ? (
          <Account onClick={handleToggle} {...selectedBankAccount} selected />
        ) : (
          <AddAccount
            onClick={() => {
              onBankAccountAdd(true);
            }}
            title="Connect new bank account"
            description="Select to connect a bank account feed"
          />
        )}
      </BankAccountSelectStyled>
      <Popper
        sx={{ zIndex: 'modal' }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        placement="bottom-start"
        slotProps={{ root: { style: { maxWidth: '540px', width: '100%' } } }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <MenuContainer>
              <ClickAwayListener onClickAway={handleClose}>
                <BankAccountSelectStyled id="account-select-menu" autoFocusItem>
                  {availableBankAccounts.map((bankAccount) => (
                    <Account
                      key={bankAccount.id}
                      onClick={() => {
                        handleMenuItemClick(bankAccount.id);
                      }}
                      {...bankAccount}
                    />
                  ))}
                  <AddAccount
                    onClick={() => {
                      onBankAccountAdd(true);
                    }}
                    title="Connect new bank account"
                    description="Select to connect a bank account feed"
                  />
                  <AddAccount
                    onClick={() => {
                      onBankAccountAdd(false);
                    }}
                    title="Add new bank account without feed"
                    description="Select if you wish to upload statement files"
                  />
                </BankAccountSelectStyled>
              </ClickAwayListener>
            </MenuContainer>
          </Grow>
        )}
      </Popper>
    </Container>
  );
};

export default BankAccountSelect;
